// Email compose URL builder. Routes to the deeplink the user has
// configured in Settings > Preferences (`emailClient` pref):
// outlook.office.com, mail.google.com (Gmail), or mailto: (default).
// Used by both the CaseView mailDlg send handler (App.jsx) and the
// Gate 4 C3b Send Pro Forma flow (ProFormaModal).

type ComposeFields = {
  to?: string;
  cc?: string;
  subject?: string;
  body?: string;
};

// Graph / deep-search result row shape (NOT a Prisma entity): the original
// sender plus to/cc recipient arrays, each { address, name }.
type ComposeAddr = { address?: string | null; name?: string | null };
type ReplyAllRow = {
  from?: ComposeAddr | null;
  to?: ComposeAddr[] | null;
  cc?: ComposeAddr[] | null;
  subject?: string | null;
};

export function buildComposeUrl(
  { to = "", cc = "", subject = "", body = "" }: ComposeFields,
  emailClient = "mailto",
) {
  const encTo = encodeURIComponent(to);
  const encCc = encodeURIComponent(cc);
  const encSubj = encodeURIComponent(subject);
  const encBody = encodeURIComponent(body);
  if (emailClient === "outlook") {
    return `https://outlook.office.com/mail/deeplink/compose?to=${encTo}&cc=${encCc}&subject=${encSubj}&body=${encBody}`;
  }
  if (emailClient === "gmail") {
    return `https://mail.google.com/mail/?view=cm&to=${encTo}&cc=${encCc}&su=${encSubj}&body=${encBody}`;
  }
  return `mailto:${encTo}?cc=${encCc}&subject=${encSubj}&body=${encBody}`;
}

export function openCompose(args: ComposeFields, emailClient?: string) {
  const url = buildComposeUrl(args, emailClient);
  window.open(url, "_blank");
}

// Phase 31.2 (item 3) — Button B ("New email to all without history").
// Builds a brand-new compose to the "to all" superset of an email row —
// the original sender PLUS every to/cc recipient — with the subject
// prefilled "RE: <original subject>" and an EMPTY body (no thread, no quote).
// FORCES the `mailto:` scheme regardless of the user's emailClient pref so it
// always hands off to the LOCAL desktop client (the "(Outlook local)" label
// promise); creates no Graph artifact, so it can never pollute the case email
// list. Row shape is the search result row: from {address,name}|null, to/cc
// arrays of {address,name}.
// 31.2 (D4) — excludeEmails drops the viewer's OWN address(es) from the
// superset (reply-all semantics: you don't re-mail yourself); seeded into
// the dedupe `seen` set so any self-address is treated as already-emitted.
export function buildLocalReplyAllUrl(
  row: ReplyAllRow | null | undefined,
  excludeEmails: string[] = [],
) {
  const addrs: string[] = [];
  const push = (a: ComposeAddr) => {
    const s = String((a && a.address) || "").trim();
    if (s) addrs.push(s);
  };
  if (row && row.from) push(row.from);
  for (const a of (row && row.to) || []) push(a);
  for (const a of (row && row.cc) || []) push(a);
  // Dedupe case-insensitively, preserve first-seen order. Pre-seed with the
  // viewer's own address(es) so they are excluded by the same pass.
  const seen = new Set(
    (excludeEmails || [])
      .map((e) => String(e || "").trim().toLowerCase())
      .filter(Boolean),
  );
  const to = addrs.filter((a) => {
    const k = a.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const orig = String((row && row.subject) || "");
  const subject = /^re:/i.test(orig.trim()) ? orig : `RE: ${orig}`;
  return buildComposeUrl(
    { to: to.join(","), cc: "", subject, body: "" },
    "mailto",
  );
}
